import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Card,
    CardContent,
    IconButton,
    Popover,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Drawer,
    useTheme,
    useMediaQuery,
    AppBar,
    Toolbar,
    Avatar,
    Fab,
    Alert,
    Autocomplete,
    Chip,
    Fade,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Send as SendIcon,
    AutoFixHigh as ImproveIcon,
    Lightbulb as EnhanceIcon,
    Compress as SimplifyIcon,
    Expand as ExpandIcon,
    Menu as MenuIcon,
    AutoAwesome as AutoAwesomeIcon,
    Settings as SettingsIcon,
    Person as PersonIcon,
    SmartToy as BotIcon,
    Refresh as RefreshIcon,
    UploadFile as UploadFileIcon,
    Tune as TuneIcon,
    School as SchoolIcon,
    Description as DescriptionIcon,
    Domain as DomainIcon,
    Business as BusinessIcon,
    Check as CheckIcon,
    AddPhotoAlternate as AddPhotoIcon,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { useCookies } from 'react-cookie';
import {
    fetchGeneratedContentAPI,
    generateContentAPI,
    getConfigOrganization,
    regenerateContentAPI,
    saveGeneratedContentAPI,
    DownloadPDFAPI,
    getUploadedResorucesAPI,
    documentImageAPI,
} from 'ServerAPI/serverAPI';
import ReactMarkdown from 'react-markdown';
import { useLocation } from 'react-router-dom';
import { SaveIcon } from 'lucide-react';
import { ContentGenerationAppBar, ContentGenerationMessages } from './content-generation-components';

const ContentGeneration = () => {
    const [cookies] = useCookies(['userId']); // Ensure userId is in cookies
    const location = useLocation();
    const { userId: routeUserId, generatedContentId: initialGeneratedContentId } = location.state || {};
    const userId = routeUserId || cookies.userId; // Prefer route, fallback to cookie

    const [generatedContentId, setGeneratedContentId] = useState(initialGeneratedContentId);
    const [isHistoryMode, setIsHistoryMode] = useState(null);
    const [isSaved, setIsSaved] = useState(false);
    const [originalMessagesCount, setOriginalMessagesCount] = useState(0);
    const [hasContentChanged, setHasContentChanged] = useState(false);

    // Configuration flag
    const ENABLE_MARKDOWN_RENDERING = false;

    // Form state
    const [question, setQuestion] = useState('');
    const [description, setDescription] = useState('');
    const [department, setDepartment] = useState('');
    const [domain, setDomain] = useState('');
    const [audienceLevel, setAudienceLevel] = useState('');
    const [title, setTitle] = useState('');

    // Organization data
    const [allDepartments, setAllDepartments] = useState([]);
    const [allProcesses, setAllProcesses] = useState([]);
    const [loading, setLoading] = useState(false);

    // File selection state
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null); // { fileId, fileName }
    const [loadingFiles, setLoadingFiles] = useState(false);

    // Chat state
    const [messages, setMessages] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [currentResponse, setCurrentResponse] = useState('');

    // Section selection state (null = all selected by default)
    const ALL_DOCUMENT_SECTIONS = [
        'Professional Introduction',
        'Learning Objectives',
        'Core Concepts and Foundations',
        'Tools, Systems, and Workflow Integration',
        'Step-by-Step Execution Guide',
        'Challenges, Risks, and Common Mistakes',
        'Best Practices and Professional Standards',
        'Key Takeaways',
        'Real-World Business Application'
    ];
    const [selectedSections, setSelectedSections] = useState(ALL_DOCUMENT_SECTIONS);
    const allSectionsSelected = selectedSections.length === ALL_DOCUMENT_SECTIONS.length;

    // UI state
    const [drawerOpen, setDrawerOpen] = useState(!initialGeneratedContentId);
    // inputMode: 'idle' = new doc | 'generated' = doc exists, hide input | 'regenerate' = show input | 'feedback' = feedback panel
    const [inputMode, setInputMode] = useState('idle');
    const [feedbackText, setFeedbackText] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Persistent WebSocket refs
    const wsRef = useRef(null);
    const responseBufferRef = useRef('');
    const isMessageCompleteRef = useRef(false);
    const pendingUserMessageIdRef = useRef(null);
    const generationTimeoutRef = useRef(null);

    // On-demand image generation state
    const [imageDialog, setImageDialog] = useState({ open: false, sectionName: '', context: '', messageId: null });
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const isImageOnlyRequestRef = useRef(false); // true while an image-only WS round-trip is in flight
    const pendingImageMessageIdRef = useRef(null); // Tracks the message ID where the image should be injected

    // Image delete confirmation modal + lightbox state
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, imgId: null });
    const [lightboxSrc, setLightboxSrc] = useState(null);

    // Store handler functions in refs so reconnection can reuse them
    const wsHandlersRef = useRef({ onmessage: null, onerror: null, onclose: null });

    const messagesEndRef = useRef(null);
    const hasScrolled = useRef(false);

    // Text selection and regeneration
    const [anchorPosition, setAnchorPosition] = useState(null);
    const [selectedText, setSelectedText] = useState('');
    const [originalContent, setOriginalContent] = useState('');
    const [originalQuestion, setOriginalQuestion] = useState('');
    const [isRegenerating, setIsRegenerating] = useState(false);

    // Loading and error states
    const [fetchingContent, setFetchingContent] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    // Helper: clear generation timeout
    const clearGenerationTimeout = () => {
        if (generationTimeoutRef.current) {
            clearTimeout(generationTimeoutRef.current);
            generationTimeoutRef.current = null;
        }
    };

    // Helper: start generation timeout (3 min safety net)
    const startGenerationTimeout = () => {
        clearGenerationTimeout();
        generationTimeoutRef.current = setTimeout(() => {
            console.error('Generation timed out after 3 minutes');
            const errorMsg = {
                id: Date.now(),
                type: 'bot',
                content: `<p style="color: #d32f2f; font-weight: bold;">Generation Timed Out</p><p>The document generation took too long. Please try again with a simpler prompt or check the server status.</p>`,
                timestamp: new Date(),
                isError: true
            };
            setMessages(prev => [...prev, errorMsg]);
            setCurrentResponse('');
            setIsGenerating(false);
            responseBufferRef.current = '';
            pendingUserMessageIdRef.current = null;
        }, 180000); // 3 minutes
    };

    // Helper: attach handlers to any WebSocket instance
    const attachWsHandlers = (socket) => {
        socket.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);

                // 'start' can mean two things:
                //   1. Server connection confirmation on first connect / reconnect
                //      (content = "Connected successfully…") — silently ignore if not generating.
                //   2. Genuine start-of-stream signal when generation is underway.
                if (message.type === 'start') {
                    const isConnectionConfirmation =
                        message.content && message.content.includes('Connected successfully');

                    if (isConnectionConfirmation) {
                        // Just a handshake — don't reset buffers or start timeout
                        console.log('WebSocket connection confirmed by server.');
                        return;
                    }

                    // Genuine generation start (or image-only request acknowledgment)
                    if (!isImageOnlyRequestRef.current) {
                        responseBufferRef.current = '';
                        isMessageCompleteRef.current = false;
                        setCurrentResponse('');
                        startGenerationTimeout();
                        if (!hasScrolled.current) {
                            setTimeout(scrollToBottom, 100);
                            hasScrolled.current = true;
                        }
                    }
                    return;
                }

                // Stream chunks — reset timeout on each chunk
                if (message.type === 'chunk' && message.content) {
                    responseBufferRef.current += message.content;
                    setCurrentResponse(prev => prev + message.content);
                    startGenerationTimeout(); // reset timer on activity
                    setTimeout(scrollToBottom, 50);
                    return;
                }

                // Handle post-stream images — inject base64 diagrams under their section headings
                if (message.type === 'images' && message.content) {
                    try {
                        const imagesMap = JSON.parse(message.content);

                        // Mark document as modified so it can be saved
                        setIsSaved(false);
                        setHasContentChanged(true);

                        const injectImages = (htmlContent) => {
                            if (!htmlContent) return htmlContent;
                            const parser = new DOMParser();
                            const doc = parser.parseFromString(htmlContent, 'text/html');
                            let updated = false;

                            Object.entries(imagesMap).forEach(([sectionName, base64Str]) => {
                                if (!base64Str) return;
                                const srcUrl = base64Str.startsWith('data:image/')
                                    ? base64Str
                                    : `data:image/png;base64,${base64Str}`;
                                // Unique ID lets us find and delete this specific image later
                                const imgId = `gen-img-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

                                // Upload to S3 immediately via PUT
                                const safeDocumentName = (title || 'untitled_document').replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').toLowerCase();
                                const safeImgId = imgId.replace(/[^a-zA-Z0-9]/g, '_');
                                const fileName = `${safeDocumentName}_${safeImgId}.png`;
                                documentImageAPI({
                                    userId: cookies.userId,
                                    orgId: cookies.organizationId,
                                    fileName,
                                    method: 'PUT',
                                    image_url: srcUrl,
                                }).then(res => {
                                    const s3Key = res?.data?.s3_key || null;
                                    console.log('[document-images] PUT success', sectionName, s3Key);
                                    if (!s3Key) return;

                                    // Stamp on live DOM (for delete button)
                                    const liveEl = document.querySelector(`[data-img-id="${imgId}"]`);
                                    if (liveEl) liveEl.setAttribute('data-s3-key', s3Key);

                                    // Also update React state so handleSave can read it from the HTML string
                                    setMessages(prev => prev.map(msg => {
                                        if (msg.type !== 'bot' || !msg.content) return msg;
                                        if (!msg.content.includes(imgId)) return msg;
                                        const p = new DOMParser();
                                        const d = p.parseFromString(msg.content, 'text/html');
                                        const el = d.querySelector(`[data-img-id="${imgId}"]`);
                                        if (el) el.setAttribute('data-s3-key', s3Key);
                                        return { ...msg, content: d.body.innerHTML };
                                    }));
                                }).catch(err => {
                                    console.error('[document-images] PUT failed', sectionName, err);
                                });

                                let matchFound = false;
                                const target = sectionName.trim().toLowerCase();

                                const insertWrap = (element) => {
                                    let insertAfter = element;
                                    while (insertAfter.parentElement && ['strong', 'b', 'span', 'em', 'i', 'a'].includes(insertAfter.tagName.toLowerCase())) {
                                        insertAfter = insertAfter.parentElement;
                                    }
                                    let sibling = insertAfter.nextElementSibling;
                                    while (sibling && sibling.classList && sibling.classList.contains('gen-img-container')) {
                                        insertAfter = sibling;
                                        sibling = sibling.nextElementSibling;
                                    }

                                    const wrap = doc.createElement('div');
                                    wrap.className = 'gen-img-container';
                                    wrap.setAttribute('data-img-id', imgId);
                                    wrap.setAttribute('data-file-name', fileName);
                                    wrap.style.cssText = 'position:relative;text-align:center;margin:16px auto;display:block;max-width:max-content;';

                                    const delBtn = doc.createElement('button');
                                    delBtn.setAttribute('data-action', 'delete-image');
                                    delBtn.innerHTML = '&times;';
                                    delBtn.title = 'Delete Image';
                                    delBtn.style.cssText = 'position:absolute;top:-10px;right:-10px;background:#ef4444;color:white;border:2px solid white;border-radius:50%;width:26px;height:26px;font-size:18px;line-height:22px;cursor:pointer;font-weight:bold;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 5px rgba(0,0,0,0.2);transition:transform 0.2s;z-index:10;';

                                    const img = doc.createElement('img');
                                    img.src = srcUrl;
                                    img.alt = sectionName;
                                    img.setAttribute('data-action', 'lightbox');
                                    img.setAttribute('data-src', srcUrl);
                                    img.style.cssText = 'max-width:100%;max-height:480px;border-radius:10px;box-shadow:0 4px 16px rgba(0,0,0,0.12);display:block;margin:0 auto;cursor:zoom-in;';

                                    wrap.appendChild(img);
                                    wrap.appendChild(delBtn);
                                    
                                    if (insertAfter === doc.body && doc.body.children.length === 0) {
                                        doc.body.appendChild(wrap);
                                    } else {
                                        insertAfter.insertAdjacentElement('afterend', wrap);
                                    }
                                    
                                    updated = true;
                                    matchFound = true;
                                };

                                // Attempt 1: Match Headings
                                doc.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach(heading => {
                                    if (matchFound) return;
                                    const headingText = heading.textContent.replace(/\s*Add image\s*$/, '').trim().toLowerCase();
                                    if (headingText === target || headingText.includes(target)) {
                                        insertWrap(heading);
                                    }
                                });

                                // Attempt 2: Fallback to bold text, list items, or paragraphs
                                if (!matchFound) {
                                    const allElements = doc.querySelectorAll('p, li, strong, b, span');
                                    for (let i = 0; i < allElements.length; i++) {
                                        const el = allElements[i];
                                        if (el.children.length === 0 || el.tagName.toLowerCase() === 'li') {
                                            const text = el.textContent.trim().toLowerCase();
                                            if (text === target || text.includes(target)) {
                                                insertWrap(el);
                                                break;
                                            }
                                        }
                                    }
                                }

                                // Attempt 3: If still not found, append to end
                                if (!matchFound) {
                                    const bodyChildren = doc.body.children;
                                    if (bodyChildren.length > 0) {
                                        insertWrap(bodyChildren[bodyChildren.length - 1]);
                                    } else {
                                        insertWrap(doc.body);
                                    }
                                }
                            });

                            return updated ? doc.body.innerHTML : htmlContent;
                        };

                        // Update the live streaming buffer
                        if (responseBufferRef.current) {
                            const patched = injectImages(responseBufferRef.current);
                            if (patched !== responseBufferRef.current) {
                                responseBufferRef.current = patched;
                                setCurrentResponse(patched);
                            }
                        }

                        // Update the already-committed bot message (if 'end' already fired)
                        setMessages(prev => {
                            const msgs = [...prev];
                            for (let i = msgs.length - 1; i >= 0; i--) {
                                if (pendingImageMessageIdRef.current && msgs[i].id !== pendingImageMessageIdRef.current) continue;
                                if (msgs[i].type === 'bot' && !msgs[i].isError) {
                                    const patched = injectImages(msgs[i].content);
                                    if (patched !== msgs[i].content) {
                                        msgs[i] = { ...msgs[i], content: patched };
                                        setOriginalContent(patched);
                                    }
                                    break;
                                }
                            }
                            return msgs;
                        });
                        pendingImageMessageIdRef.current = null;

                    } catch (err) {
                        console.error('Failed to inject section images:', err);
                    }
                    return;
                }






                // Stream complete - ADD BOT MESSAGE
                if (message.type === 'end') {
                    clearGenerationTimeout();

                    // Image-only flow: don't add a new bot message, just reset state
                    if (isImageOnlyRequestRef.current) {
                        setIsGeneratingImage(false);
                        isImageOnlyRequestRef.current = false;
                        return;
                    }

                    if (responseBufferRef.current.trim() && !isMessageCompleteRef.current) {
                        const botMessage = {
                            id: Date.now(),
                            type: 'bot',
                            content: responseBufferRef.current.trim(),
                            timestamp: new Date()
                        };
                        setMessages(prev => [...prev, botMessage]);
                        setOriginalContent(responseBufferRef.current.trim());
                        isMessageCompleteRef.current = true;
                    }
                    setCurrentResponse('');
                    setIsGenerating(false);
                    setQuestion('');
                    responseBufferRef.current = '';
                    pendingUserMessageIdRef.current = null;
                    setInputMode('generated'); // hide input, show action bar
                    return;
                }

                // Error handling
                if (message.type === 'error') {
                    clearGenerationTimeout();
                    console.error('Server error:', message.error);
                    const errorMsg = {
                        id: Date.now(),
                        type: 'bot',
                        content: `<p style="color: #d32f2f; font-weight: bold;">Error: ${message.error || 'Internal server error'}</p><p>The server encountered an issue while generating your document. Please try again or refine your prompt.</p>`,
                        timestamp: new Date(),
                        isError: true
                    };
                    setMessages(prev => [...prev, errorMsg]);
                    setCurrentResponse('');
                    setIsGenerating(false);
                    pendingUserMessageIdRef.current = null;
                    return;
                }
            } catch (err) {
                console.error('WebSocket message parse error:', err);
                clearGenerationTimeout();
                setIsGenerating(false);
                setCurrentResponse('');
            }
        };

        socket.onerror = (err) => {
            console.error('WebSocket error:', err);
            clearGenerationTimeout();
            const errorMsg = {
                id: Date.now(),
                type: 'bot',
                content: `<p style="color: #d32f2f; font-weight: bold;">Connection Error</p><p>Failed to connect to the generation service. Please check your network and try again.</p>`,
                timestamp: new Date(),
                isError: true
            };
            setMessages(prev => [...prev, errorMsg]);
            setIsGenerating(false);
            setCurrentResponse('');
        };

        socket.onclose = () => {
            console.log('WebSocket closed - auto-reconnect on next message');
            wsRef.current = null;
        };
    };

    // === PERSISTENT WEBSOCKET SETUP (ONE CONNECTION FOR LIFE) ===
    useEffect(() => {
        const wsUrl = generateContentAPI();
        const socket = new WebSocket(wsUrl);

        socket.onopen = () => {
            console.log('PERMANENT WebSocket CONNECTED - Ready for unlimited messages');
            wsRef.current = socket;
        };

        attachWsHandlers(socket);

        // Cleanup
        return () => {
            clearGenerationTimeout();
            if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
                socket.close();
            }
        };
    }, []);

    // Fetch existing generated content
    const fetchExistingContent = async () => {
        if (!userId || !generatedContentId) return;

        try {
            setFetchingContent(true);
            setFetchError(null);

            const response = await fetchGeneratedContentAPI({
                userId,
                generatedContentId
            });

            if (response?.status === 200 && response?.data?.content) {
                const content = response.data.content;

                if (content.title) setTitle(content.title);
                if (content.description) setDescription(content.description);
                if (content.department) setDepartment(content.department);
                if (content.domain) setDomain(content.domain);
                if (content.audience_level) setAudienceLevel(content.audience_level);

                if (content.fileId) {
                    setSelectedFile({ fileId: content.fileId, fileName: content.fileName || 'Unknown' });
                }

                if (content.chat && Array.isArray(content.chat)) {
                    const transformedMessages = content.chat.flatMap(chatItem => {
                        let botContent = chatItem.bot;

                        if (chatItem.images && Object.keys(chatItem.images).length > 0) {
                            try {
                                const parser = new DOMParser();
                                const doc = parser.parseFromString(botContent, 'text/html');
                                let updated = false;

                                let imagesArray = [];
                                if (Array.isArray(chatItem.images)) {
                                    chatItem.images.forEach(imgObj => {
                                        Object.entries(imgObj).forEach(([sectionName, url]) => {
                                            imagesArray.push({ sectionName, url });
                                        });
                                    });
                                } else {
                                    Object.entries(chatItem.images).forEach(([sectionName, url]) => {
                                        imagesArray.push({ sectionName, url });
                                    });
                                }

                                imagesArray.forEach(({ sectionName, url }) => {
                                    if (!url) return;

                                    let s3Key = '';
                                    try {
                                        const urlObj = new URL(url);
                                        s3Key = decodeURIComponent(urlObj.pathname.replace(/^\//, ''));
                                    } catch (err) {
                                        console.warn('Could not parse s3_key from url:', url);
                                    }

                                    const imgId = `gen-img-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

                                    let matchFound = false;
                                    const target = sectionName.trim().toLowerCase();

                                    const insertWrap = (element) => {
                                        let insertAfter = element;
                                        while (insertAfter.parentElement && ['strong', 'b', 'span', 'em', 'i', 'a'].includes(insertAfter.tagName.toLowerCase())) {
                                            insertAfter = insertAfter.parentElement;
                                        }
                                        let sibling = insertAfter.nextElementSibling;
                                        while (sibling && sibling.classList && sibling.classList.contains('gen-img-container')) {
                                            insertAfter = sibling;
                                            sibling = sibling.nextElementSibling;
                                        }

                                        const wrap = doc.createElement('div');
                                        wrap.className = 'gen-img-container';
                                        wrap.setAttribute('data-img-id', imgId);
                                        wrap.setAttribute('data-file-name', sectionName);
                                        if (s3Key) {
                                            wrap.setAttribute('data-s3-key', s3Key);
                                        }
                                        wrap.style.cssText = 'position:relative;text-align:center;margin:16px auto;display:block;max-width:max-content;';

                                        const delBtn = doc.createElement('button');
                                        delBtn.setAttribute('data-action', 'delete-image');
                                        delBtn.innerHTML = '&times;';
                                        delBtn.title = 'Delete Image';
                                        delBtn.style.cssText = 'position:absolute;top:-10px;right:-10px;background:#ef4444;color:white;border:2px solid white;border-radius:50%;width:26px;height:26px;font-size:18px;line-height:22px;cursor:pointer;font-weight:bold;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 5px rgba(0,0,0,0.2);transition:transform 0.2s;z-index:10;';

                                        const img = doc.createElement('img');
                                        img.src = url;
                                        img.alt = sectionName;
                                        img.setAttribute('data-action', 'lightbox');
                                        img.setAttribute('data-src', url);
                                        img.style.cssText = 'max-width:100%;max-height:480px;border-radius:10px;box-shadow:0 4px 16px rgba(0,0,0,0.12);display:block;margin:0 auto;cursor:zoom-in;';

                                        wrap.appendChild(img);
                                        wrap.appendChild(delBtn);
                                        
                                        if (insertAfter === doc.body && doc.body.children.length === 0) {
                                            doc.body.appendChild(wrap);
                                        } else {
                                            insertAfter.insertAdjacentElement('afterend', wrap);
                                        }
                                        
                                        updated = true;
                                        matchFound = true;
                                    };

                                    // Attempt 1: Match Headings
                                    doc.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach(heading => {
                                        if (matchFound) return;
                                        const headingText = heading.textContent.replace(/\s*Add image\s*$/, '').trim().toLowerCase();
                                        if (headingText === target || headingText.includes(target)) {
                                            insertWrap(heading);
                                        }
                                    });

                                    // Attempt 2: Fallback to bold text, list items, or paragraphs
                                    if (!matchFound) {
                                        const allElements = doc.querySelectorAll('p, li, strong, b, span');
                                        for (let i = 0; i < allElements.length; i++) {
                                            const el = allElements[i];
                                            if (el.children.length === 0 || el.tagName.toLowerCase() === 'li') {
                                                const text = el.textContent.trim().toLowerCase();
                                                if (text === target || text.includes(target)) {
                                                    insertWrap(el);
                                                    break;
                                                }
                                            }
                                        }
                                    }

                                    // Attempt 3: If still not found, append to end
                                    if (!matchFound) {
                                        const bodyChildren = doc.body.children;
                                        if (bodyChildren.length > 0) {
                                            insertWrap(bodyChildren[bodyChildren.length - 1]);
                                        } else {
                                            insertWrap(doc.body);
                                        }
                                    }
                                });

                                if (updated) {
                                    botContent = doc.body.innerHTML;
                                }
                            } catch (e) {
                                console.error('Error injecting images into fetched content:', e);
                            }
                        }

                        return [
                            {
                                id: crypto.randomUUID(),
                                type: 'user',
                                content: chatItem.user,
                                timestamp: chatItem.date
                            },
                            {
                                id: crypto.randomUUID(),
                                type: 'bot',
                                content: botContent,
                                timestamp: chatItem.date
                            }
                        ];
                    });
                    setMessages(transformedMessages);
                    setOriginalMessagesCount(transformedMessages.length);
                }

                setIsHistoryMode(true);
                setIsSaved(true);
                setHasContentChanged(false);
                setInputMode('generated');
            } else {
                setIsHistoryMode(null);
                setFetchError('Failed to fetch content details');
            }
        } catch (error) {
            console.error('Error fetching existing content:', error);
            setIsHistoryMode(null);
            setFetchError(error?.response?.data?.message || 'An error occurred while fetching content');
        } finally {
            setFetchingContent(false);
        }
    };

    // Fetch organization details
    const fetchOrganisationDetails = async () => {
        try {
            setLoading(true);
            const details = await getConfigOrganization(
                cookies?.email,
                cookies?.organizationId,
                userId
            );

            if (details?.status === 200) {
                setAllDepartments(details?.data?.department || []);
                setAllProcesses(details?.data?.domain || []);
            } else {
                console.error("Failed to fetch organization details:", details);
            }
        } catch (error) {
            console.error("Error fetching organization details:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch uploaded files
    const fetchUploadedFiles = async () => {
        if (!userId) return;

        try {
            setLoadingFiles(true);
            const response = await getUploadedResorucesAPI(userId);

            const resources = response.data?.resources || response.data?.content || [];
            const mappedFiles = resources
                .filter(item => item.vectorSpace === true)
                .map(item => ({
                    fileId: item.fileId,
                    fileName: item.contentFileName?.replace(/\.pdf$/i, '') || 'Untitled',
                    fullName: item.contentFileName
                }))
                .sort((a, b) => a.fileName.localeCompare(b.fileName));

            setUploadedFiles(mappedFiles);
        } catch (err) {
            console.error('Error fetching uploaded files:', err);
            setUploadedFiles([]);
        } finally {
            setLoadingFiles(false);
        }
    };

    // Handle save
    const handleSave = async () => {
        try {
            setIsSaving(true);

            const chatData = [];

            const validMessages = messages.filter(m => !m.isError);
            for (let i = 0; i < validMessages.length; i += 2) {
                if (validMessages[i] && validMessages[i + 1]) {
                    const botRaw = validMessages[i + 1].content || '';

                    // Parse bot HTML, extract image metadata per chat entry, then strip containers
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(botRaw, 'text/html');

                    const chatImages = []; // Array of { section_name: s3_key } scoped to this chat pair
                    doc.querySelectorAll('.gen-img-container').forEach(container => {
                        const s3Key = container.getAttribute('data-s3-key');
                        const imgEl = container.querySelector('img');
                        const sectionName = imgEl?.getAttribute('alt') || container.getAttribute('data-file-name') || 'unknown';
                        if (s3Key) {
                            chatImages.push({ [sectionName]: s3Key });
                        }
                        container.remove(); // strip image from bot content
                    });

                    const cleanedBot = doc.body.innerHTML;

                    chatData.push({
                        user: validMessages[i].content,
                        bot: cleanedBot,
                        date: validMessages[i + 1].timestamp || new Date().toISOString(),
                        images: chatImages,
                    });
                }
            }

            const payload = {
                userId: userId,
                generatedContentId: generatedContentId,
                title: title,
                department: department,
                domain: domain,
                audience_level: audienceLevel,
                description: description,
                chat: chatData,
                fileName: selectedFile?.fullName || null,
            };

            const response = await saveGeneratedContentAPI(payload);

            if (response.data.message) {
                setIsSaved(true);
                setHasContentChanged(false);
                setOriginalMessagesCount(messages.length);
                setGeneratedContentId(response.data.generatedContentId);
                console.log('Content saved successfully');
            } else {
                throw new Error('Failed to save content');
            }
        } catch (error) {
            console.error('Error saving content:', error);
        } finally {
            setIsSaving(false);
        }
    };

    // Handle download — chatNos is a list of chat[] indices to include in the PDF
    const handleDownload = async (chatNos) => {
        if (!isSaved || !generatedContentId) {
            console.warn("Cannot download: Content must be saved first");
            return;
        }

        try {
            setIsDownloading(true);

            const payload = {
                doc_id: generatedContentId,
                // Only include chat_no when a specific selection is made (not all)
                ...(chatNos && chatNos.length > 0 ? { chat_no: chatNos } : {})
            };
            const response = await DownloadPDFAPI(payload);

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${title || 'chat-conversation'}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            console.log('PDF downloaded successfully');
        } catch (error) {
            console.error('Error downloading PDF:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    // Effects
    useEffect(() => {
        fetchOrganisationDetails();
        fetchExistingContent();
    }, [generatedContentId]);

    useEffect(() => {
        if (userId) {
            fetchUploadedFiles();
        }
    }, [userId]);

    useEffect(() => {
        if (messages.length > 0 && !isHistoryMode) {
            setIsSaved(false);
        }
        if (title === "") {
            setTitle(question);
        }
    }, [messages, isHistoryMode]);

    useEffect(() => {
        if (isHistoryMode && originalMessagesCount > 0) {
            if (messages.length > originalMessagesCount) {
                setHasContentChanged(true);
                setIsSaved(false);
            }
        }
    }, [messages, isHistoryMode, originalMessagesCount]);

    useEffect(() => {
        if (isHistoryMode && hasContentChanged) {
            setIsSaved(false);
        }
    }, [question, description, department, domain, audienceLevel, isHistoryMode, hasContentChanged]);

    // Scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // === IMAGE DELETE & LIGHTBOX — listen for custom events from RenderLatex ===
    useEffect(() => {
        const handleImgDelete = (e) => setDeleteConfirm({ open: true, imgId: e.detail.imgId });
        const handleImgLightbox = (e) => setLightboxSrc(e.detail.src);
        document.addEventListener('section-image-delete', handleImgDelete);
        document.addEventListener('section-image-lightbox', handleImgLightbox);
        return () => {
            document.removeEventListener('section-image-delete', handleImgDelete);
            document.removeEventListener('section-image-lightbox', handleImgLightbox);
        };
    }, []);

    /** Remove the image with the given data-img-id from the message that contains it */
    const confirmDeleteImage = () => {
        const { imgId } = deleteConfirm;

        // Find the s3_key and fileName stored on the container before removing it from state
        const liveContainer = document.querySelector(`[data-img-id="${imgId}"]`);
        const s3Key = liveContainer?.getAttribute('data-s3-key') || null;
        const fileName = liveContainer?.getAttribute('data-file-name') || imgId;

        // Delete from S3 if we have a key
        if (s3Key) {
            documentImageAPI({
                userId: cookies.userId,
                orgId: cookies.organizationId,
                fileName,
                method: 'DELETE',
                s3_key: s3Key,
            }).then(() => {
                console.log('[document-images] DELETE success', imgId);
            }).catch(err => {
                console.error('[document-images] DELETE failed', imgId, err);
            });
        }

        setMessages(prev => prev.map(msg => {
            if (msg.type !== 'bot' || !msg.content) return msg;
            const parser = new DOMParser();
            const doc = parser.parseFromString(msg.content, 'text/html');
            const container = doc.querySelector(`[data-img-id="${imgId}"]`);
            if (!container) return msg;
            container.remove();
            return { ...msg, content: doc.body.innerHTML };
        }));
        setDeleteConfirm({ open: false, imgId: null });
        setIsSaved(false);
    };

    // === ON-DEMAND SECTION IMAGE GENERATION ===
    /** Called by RenderLatex when user clicks the image icon on a heading */
    const handleSectionImageRequest = (sectionName, messageId) => {
        setImageDialog({ open: true, sectionName, context: '', messageId });
    };

    /** Send the image-only WS payload after the user confirms the dialog */
    const sendImageRequest = () => {
        const { sectionName, context, messageId } = imageDialog;
        if (!sectionName) return;

        setIsGeneratingImage(true);
        isImageOnlyRequestRef.current = true;
        pendingImageMessageIdRef.current = messageId || null;
        setImageDialog(prev => ({ ...prev, open: false }));

        // Extract section's generated content from the latest bot message or currentResponse
        let extractedContent = null;
        try {
            let botContent = '';
            for (let i = messages.length - 1; i >= 0; i--) {
                // If messageId is available, extract from that specific message; else use latest
                if (messageId && messages[i].id !== messageId) continue;
                if (messages[i].type === 'bot' && !messages[i].isError && messages[i].content) {
                    botContent = messages[i].content;
                    break;
                }
            }
            if (!botContent && currentResponse) {
                botContent = currentResponse;
            }

            if (botContent) {
                // Strip code fences just in case (like in RenderLatex)
                let cleanHtml = String(botContent);
                cleanHtml = cleanHtml.replace(/^```(?:html)?\s*\n?/i, '').replace(/\n?```\s*$/i, '');

                const parser = new DOMParser();
                const doc = parser.parseFromString(cleanHtml, 'text/html');
                const headings = doc.querySelectorAll('h1,h2,h3,h4,h5,h6');

                // Helper to normalize strings for robust, numbering-agnostic matching
                const normalize = (str) => {
                    return str
                        .toLowerCase()
                        .replace(/^\d+[\.\s\-:]+/, '') // remove leading numbering like "1. ", "1- ", "1: ", etc.
                        .replace(/[^a-z0-9]/g, '') // remove all non-alphanumeric characters
                        .trim();
                };

                const targetNormalized = normalize(sectionName);
                let targetHeading = null;
                for (const heading of headings) {
                    const headingTextClean = heading.textContent.replace(/\s*Add image\s*$/, '').trim();
                    const headingNormalized = normalize(headingTextClean);
                    if (headingNormalized === targetNormalized || headingNormalized.includes(targetNormalized) || targetNormalized.includes(headingNormalized)) {
                        targetHeading = heading;
                        break;
                    }
                }

                if (targetHeading) {
                    const getHeadingLevel = (tagName) => {
                        const match = tagName.match(/^H([1-6])$/i);
                        return match ? parseInt(match[1], 10) : 999;
                    };
                    const targetLevel = getHeadingLevel(targetHeading.tagName);
                    const sectionNodes = [];
                    let sibling = targetHeading.nextElementSibling;
                    while (sibling) {
                        const siblingLevel = getHeadingLevel(sibling.tagName);
                        if (siblingLevel <= targetLevel) {
                            break;
                        }
                        
                        // Safely check if the sibling is an image container or button
                        const isImgContainer = sibling.classList && sibling.classList.contains('gen-img-container');
                        if (!isImgContainer) {
                            const clone = sibling.cloneNode(true);
                            clone.querySelectorAll('.section-img-btn').forEach(btn => btn.remove());
                            sectionNodes.push(clone.outerHTML || clone.textContent);
                        }
                        sibling = sibling.nextElementSibling;
                    }
                    if (sectionNodes.length > 0) {
                        extractedContent = sectionNodes.join('\n');
                    }
                }

                console.log('DOM Content Extraction: Section =', sectionName, 'Found heading =', !!targetHeading, 'Extracted Content length =', extractedContent ? extractedContent.length : 0);
            }
        } catch (err) {
            console.error('Failed to extract section content:', err);
        }

        const payload = {
            userId: cookies.userId,
            image_requests: [{
                section_name: sectionName,
                context: context || null,
                generated_content: extractedContent || null
            }],
        };

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(payload));
        } else {
            setIsGeneratingImage(false);
            isImageOnlyRequestRef.current = false;
            console.error('WebSocket not connected — cannot generate image on demand');
        }
    };


    // === HANDLE SUBMIT - USE PERSISTENT WS ===
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!question.trim()) {
            console.warn("Question is required");
            return;
        }

        if (!selectedFile && (!department || !domain)) {
            console.warn("Domain and Department required when no file is selected");
            return;
        }

        const userMessageId = crypto.randomUUID();
        pendingUserMessageIdRef.current = userMessageId;

        const userMessage = {
            id: userMessageId,
            type: 'user',
            content: question,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setIsGenerating(true);
        setInputMode('idle');
        setCurrentResponse('');
        hasScrolled.current = false;
        setOriginalQuestion(question);
        responseBufferRef.current = '';
        isMessageCompleteRef.current = false;


        const payload = {
            question,
            department,
            domain,
            audience_level: audienceLevel,
            description,
            fileName: selectedFile?.fullName || null,
            userId: cookies.userId,
            selected_sections: allSectionsSelected ? null : selectedSections
        };

        const sendPayload = () => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify(payload));
                console.log('Message sent on persistent connection');
            } else {
                console.log('Reconnecting WebSocket...');
                const wsUrl = generateContentAPI();
                const socket = new WebSocket(wsUrl);
                socket.onopen = () => {
                    wsRef.current = socket;
                    socket.send(JSON.stringify(payload));
                    console.log('Reconnected and sent');
                };
                // Attach proper handlers to the new socket
                attachWsHandlers(socket);
            }
        };

        sendPayload();
    };

    // Helper to get the latest VALID bot document response.
    // Excludes: error messages, connection confirmations, timeout notices.
    const getLatestDocumentContent = () => {
        const SYSTEM_STRINGS = [
            'Connected successfully',
            'Connection Error',
            'Generation Timed Out',
            'Failed to connect',
            'Send your message to start',
        ];

        const validBotMessages = messages.filter(m => {
            if (m.type !== 'bot') return false;
            if (m.isError) return false;
            if (!m.content || !m.content.trim()) return false;
            // Exclude any message whose content looks like a system/status string
            if (SYSTEM_STRINGS.some(s => m.content.includes(s))) return false;
            return true;
        });

        if (validBotMessages.length > 0) {
            const rawContent = validBotMessages[validBotMessages.length - 1].content;
            
            // Parse the HTML and remove image containers to prevent large base64 strings
            // from being sent back as context, which causes token limit exceeded errors.
            const parser = new DOMParser();
            const doc = parser.parseFromString(rawContent, 'text/html');
            doc.querySelectorAll('.gen-img-container, img').forEach(el => el.remove());
            
            return doc.body.innerHTML;
        }
        return '';
    };

    // Dedicated feedback submission handler with updated payload structure
    const handleFeedbackSubmit = (textToSubmit) => {
        const feedbackPrompt = textToSubmit || feedbackText;
        if (!feedbackPrompt.trim()) return;

        const userMessageId = crypto.randomUUID();
        pendingUserMessageIdRef.current = userMessageId;

        const userMessage = {
            id: userMessageId,
            type: 'user',
            content: `Feedback: ${feedbackPrompt}`,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setIsGenerating(true);
        setInputMode('idle');
        setCurrentResponse('');
        setFeedbackText('');
        hasScrolled.current = false;
        responseBufferRef.current = '';
        isMessageCompleteRef.current = false;

        const latestDocContent = getLatestDocumentContent();

        const payload = {
            feedback: feedbackPrompt,
            context: latestDocContent,
            department,
            domain,
            audience_level: audienceLevel,
            description,
            fileName: selectedFile?.fullName || null,
            userId: cookies.userId,
            selected_sections: allSectionsSelected ? null : selectedSections
        };

        const sendPayload = () => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify(payload));
                console.log('Feedback message sent on persistent connection');
            } else {
                console.log('Reconnecting WebSocket for feedback...');
                const wsUrl = generateContentAPI();
                const socket = new WebSocket(wsUrl);
                socket.onopen = () => {
                    wsRef.current = socket;
                    socket.send(JSON.stringify(payload));
                    console.log('Reconnected and sent feedback');
                };
                attachWsHandlers(socket);
            }
        };

        sendPayload();
    };

    // Handle regeneration
    const handleRegenerate = async (improvementType) => {
        setAnchorPosition(null);
        setIsRegenerating(true);

        const payload = {
            original_content: originalContent,
            selected_text: selectedText,
            improvement_type: improvementType,
            department,
            domain,
            description,
            original_question: originalQuestion,
            fileName: selectedFile?.fullName || undefined,
            userId: cookies.userId
        };

        try {
            const response = await regenerateContentAPI(payload);
            setMessages(prev =>
                prev.map((msg, index) =>
                    index === prev.length - 1 && msg.type === 'bot'
                        ? { ...msg, content: response.data.updated_content }
                        : msg
                )
            );
            setOriginalContent(response.data.updated_content);
        } catch (error) {
            console.error('Error regenerating content:', error);
        } finally {
            setIsRegenerating(false);
        }
    };

    const improvementOptions = [
        { type: 'improve', label: 'Improve', icon: <ImproveIcon /> },
        { type: 'enhance', label: 'Enhance', icon: <EnhanceIcon /> },
        { type: 'simplify', label: 'Simplify', icon: <SimplifyIcon /> },
        { type: 'expand', label: 'Expand', icon: <ExpandIcon /> }
    ];
    // Drawer content
    const drawerContent = (
        <Box sx={{ width: isMobile ? '100vw' : 380, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#f8f9fb' }}>
            {/* Header */}
            <Box sx={{
                px: 3, py: 2.5,
                background: 'linear-gradient(135deg, #23479F 0%, #3b63c7 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TuneIcon sx={{ fontSize: 22, opacity: 0.9 }} />
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                                Document Settings
                            </Typography>
                            <Tooltip
                                title="Choose from pre-uploaded files here. To add new source files, upload them in the Content Generation Resources section."
                                arrow
                                placement="bottom"
                            >
                                <IconButton size="small" sx={{ color: 'white', p: 0.2, opacity: 0.8, '&:hover': { opacity: 1 } }}>
                                    <InfoIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.7rem' }}>
                            Configure before generating
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={() => setDrawerOpen(false)}
                    size="small"
                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                {/* Section 1: Source File */}
                <Box sx={{
                    bgcolor: 'white',
                    borderRadius: 3,
                    border: '1px solid #e8ecf1',
                    p: 2.5,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: 2,
                            bgcolor: '#ede7f6', display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <UploadFileIcon sx={{ fontSize: 18, color: '#5e35b1' }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                            Source File
                        </Typography>
                    </Box>
                    <Autocomplete
                        options={uploadedFiles}
                        getOptionLabel={(option) => option.fileName}
                        value={selectedFile}
                        onChange={(event, newValue) => {
                            setSelectedFile(newValue);
                            if (!newValue) {
                                setDomain('');
                                setDepartment('');
                                setDescription('');
                            }
                        }}
                        loading={loadingFiles}
                        disabled={isHistoryMode === true}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Search and select a file..."
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingFiles ? <CircularProgress color="inherit" size={18} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                    sx: { borderRadius: 2, bgcolor: '#fafbfc', fontSize: '0.875rem' }
                                }}
                            />
                        )}
                        noOptionsText="No processed files found"
                        fullWidth
                    />
                    {selectedFile && (
                        <Box sx={{ mt: 1.5, px: 1.5, py: 1, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #bbdefb' }}>
                            <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 500, lineHeight: 1.4 }}>
                                ℹ️ Domain, Department & Description auto-configured from file.
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* OR Divider */}
                {!selectedFile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mx: 1 }}>
                        <Divider sx={{ flex: 1, borderColor: '#d0d7de' }} />
                        <Chip
                            label="OR"
                            size="small"
                            sx={{
                                height: 24, fontSize: '0.65rem', fontWeight: 700,
                                bgcolor: '#eef1f6', color: '#64748b',
                                letterSpacing: '0.08em',
                                border: '1px solid #d0d7de'
                            }}
                        />
                        <Divider sx={{ flex: 1, borderColor: '#d0d7de' }} />
                    </Box>
                )}

                {/* Section 2: Manual Configuration (Domain, Department, Level, Description) */}
                <Box sx={{
                    bgcolor: 'white',
                    borderRadius: 3,
                    border: '1px solid #e8ecf1',
                    p: 2.5,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    opacity: selectedFile ? 0.55 : 1,
                    pointerEvents: selectedFile ? 'none' : 'auto',
                    transition: 'opacity 0.2s ease'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: 2,
                            bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <TuneIcon sx={{ fontSize: 18, color: '#1565c0' }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                            Manual Configuration
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Domain */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Domain</InputLabel>
                            <Select
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                label="Domain"
                                disabled={isHistoryMode === true || !!selectedFile}
                                sx={{ borderRadius: 2, bgcolor: '#fafbfc' }}
                            >
                                {loading ? (
                                    <MenuItem disabled>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CircularProgress size={16} />
                                            <Typography variant="body2">Loading...</Typography>
                                        </Box>
                                    </MenuItem>
                                ) : (
                                    allProcesses.map((proc) => (
                                        <MenuItem key={proc.title} value={proc.title}>
                                            {proc.title}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        {/* Department */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Department</InputLabel>
                            <Select
                                value={department}
                                onChange={(e) => setDepartment(e.target.value)}
                                label="Department"
                                disabled={isHistoryMode === true || !!selectedFile}
                                sx={{ borderRadius: 2, bgcolor: '#fafbfc' }}
                            >
                                {loading ? (
                                    <MenuItem disabled>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CircularProgress size={16} />
                                            <Typography variant="body2">Loading...</Typography>
                                        </Box>
                                    </MenuItem>
                                ) : (
                                    allDepartments.map((dept) => (
                                        <MenuItem key={dept.title} value={dept.title}>
                                            {dept.title}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        <Divider sx={{ borderColor: '#f0f2f5' }} />

                        {/* Audience Level */}
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', mb: 0.5, display: 'block' }}>
                                Audience Level
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={audienceLevel}
                                    onChange={(e) => setAudienceLevel(e.target.value)}
                                    displayEmpty
                                    disabled={isHistoryMode === true}
                                    sx={{ borderRadius: 2, bgcolor: '#fafbfc' }}
                                >
                                    <MenuItem value="" disabled><em>Select level...</em></MenuItem>
                                    <MenuItem value="Beginner">🟢 Beginner</MenuItem>
                                    <MenuItem value="Intermediate">🟡 Intermediate</MenuItem>
                                    <MenuItem value="Advanced">🔴 Advanced</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Divider sx={{ borderColor: '#f0f2f5' }} />

                        {/* Additional Context */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                                    Additional Context
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                                    (Optional)
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                multiline
                                rows={3}
                                size="small"
                                placeholder="e.g. Focus on practical examples, include compliance notes..."
                                disabled={!!selectedFile}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: '#fafbfc',
                                        fontSize: '0.875rem'
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

            </Box>

            {/* Footer hint */}
            <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e8ecf1', bgcolor: 'white', flexShrink: 0 }}>
                <Typography variant="caption" sx={{ color: '#94a3b8', lineHeight: 1.5, display: 'block', textAlign: 'center' }}>
                    Select a file <b>or</b> configure Domain & Department to enable document generation.
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Box
            sx={{
                position: 'fixed',
                top: '70px',
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: '#f5f7fa',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                zIndex: 1,
            }}
        >
            <ContentGenerationAppBar
                setDrawerOpen={setDrawerOpen}
                domain={domain}
                department={department}
                messages={messages}
                isSaved={isSaved}
                setIsSaved={setIsSaved}
                isSaving={isSaving}
                isDownloading={isDownloading}
                handleSave={handleSave}
                handleDownload={handleDownload}
                isMobile={isMobile}
                title={title}
                onTitleChange={setTitle}
            />

            {fetchingContent && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Loading content...
                        </Typography>
                    </Box>
                </Box>
            )}

            {fetchError && (
                <Alert severity="error" sx={{ m: 2 }}>
                    {fetchError}
                </Alert>
            )}

            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                variant={isMobile ? 'temporary' : 'temporary'}
                sx={{
                    '& .MuiDrawer-paper': {
                        bgcolor: 'background.default',
                        borderRight: '1px solid #e0e0e0'
                    }
                }}
            >
                {drawerContent}
            </Drawer>

            <ContentGenerationMessages
                messages={messages}
                isGenerating={isGenerating}
                isRegenerating={isRegenerating}
                currentResponse={currentResponse}
                messagesEndRef={messagesEndRef}
                ENABLE_MARKDOWN_RENDERING={ENABLE_MARKDOWN_RENDERING}
                onMessageEdit={(messageId, newContent) => {
                    setMessages(prev => prev.map(msg =>
                        msg.id === messageId ? { ...msg, content: newContent } : msg
                    ));
                }}
                onMessageCopy={(content) => {
                    console.log('Copied:', content);
                }}
                onMessageDelete={(messageId) => {
                    setMessages(messages => messages.filter(msg => msg.id !== messageId));
                }}
                setIsSaved={setIsSaved}
                isSaved={isSaved}
                onSectionImageRequest={handleSectionImageRequest}
            />

            {/* ─── BOTTOM BAR ─── */}
            <Paper
                elevation={0}
                sx={{
                    bgcolor: 'white',
                    borderTop: '1px solid #e8ecf1',
                    flexShrink: 0,
                    position: 'sticky',
                    bottom: 0,
                    width: '100%',
                    zIndex: 1000
                }}
            >

                {/* ── MODE: generated → show action bar ── */}
                <Fade in={inputMode === 'generated' && !isGenerating} timeout={400} unmountOnExit>
                    <Box sx={{
                        px: 3, py: 2.5, maxWidth: 900, mx: 'auto',
                        display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap',
                        animation: 'slideUp 0.35s cubic-bezier(0.22,1,0.36,1)',
                        '@keyframes slideUp': {
                            from: { opacity: 0, transform: 'translateY(10px)' },
                            to: { opacity: 1, transform: 'translateY(0)' }
                        }
                    }}>
                        {/* Regenerate card */}
                        <Box sx={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
                            p: 2, borderRadius: 3, border: '1.5px solid #dde5f4',
                            bgcolor: '#f8faff', minWidth: 200, maxWidth: 240,
                            transition: 'all 0.2s ease',
                            '&:hover': { borderColor: '#23479F', bgcolor: '#f0f4ff', boxShadow: '0 4px 16px rgba(35,71,159,0.1)', transform: 'translateY(-2px)' }
                        }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={() => {
                                    setQuestion(originalQuestion);
                                    setInputMode('regenerate');
                                }}
                                sx={{
                                    borderRadius: 2, textTransform: 'none', fontWeight: 600,
                                    px: 2.5, py: 1,
                                    borderColor: '#23479F', color: '#23479F', bgcolor: 'transparent',
                                    '&:hover': { bgcolor: 'transparent', borderColor: '#1b3780' }
                                }}
                            >
                                Regenerate
                            </Button>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem', textAlign: 'center', lineHeight: 1.5 }}>
                                Not happy with the overall content? Regenerate a fresh version from scratch.
                            </Typography>
                        </Box>

                        {/* Give Feedback card */}
                        <Box sx={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
                            p: 2, borderRadius: 3, border: '1.5px solid #c7d4f5',
                            bgcolor: '#f0f4ff', minWidth: 200, maxWidth: 240,
                            transition: 'all 0.2s ease',
                            '&:hover': { borderColor: '#1b3780', bgcolor: '#e8eefb', boxShadow: '0 4px 16px rgba(35,71,159,0.15)', transform: 'translateY(-2px)' }
                        }}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<AutoAwesomeIcon />}
                                onClick={() => { setFeedbackText(''); setInputMode('feedback'); }}
                                sx={{
                                    borderRadius: 2, textTransform: 'none', fontWeight: 600,
                                    px: 2.5, py: 1,
                                    background: 'linear-gradient(135deg, #23479F 0%, #3b63c7 100%)',
                                    boxShadow: '0 4px 12px rgba(35,71,159,0.25)',
                                    '&:hover': { boxShadow: '0 6px 16px rgba(35,71,159,0.35)' }
                                }}
                            >
                                Give Feedback
                            </Button>
                            <Typography variant="caption" sx={{ color: '#475569', fontSize: '0.72rem', textAlign: 'center', lineHeight: 1.5 }}>
                                Edit section titles, add or remove sections, or refine specific content.
                            </Typography>
                        </Box>
                    </Box>
                </Fade>

                {/* ── MODE: feedback ── */}
                <Fade in={inputMode === 'feedback' && !isGenerating} timeout={300} unmountOnExit>
                    <Box sx={{ px: 3, py: 2, maxWidth: 900, mx: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', letterSpacing: '0.02em' }}>
                                WHAT WOULD YOU LIKE TO CHANGE?
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => setInputMode('generated')}
                                sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.72rem', color: '#94a3b8' }}
                            >
                                Cancel
                            </Button>
                        </Box>

                        {/* Feedback example chips */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                            {[
                                'Remove a section',
                                'Add a custom section',
                                'Rename section title',
                                'Simplify the language',
                                'Make it more detailed',
                                'Add practical examples',
                                'Rewrite Key Takeaways',
                                'Make it beginner-friendly'
                            ].map(example => (
                                <Chip
                                    key={example}
                                    label={example}
                                    size="small"
                                    onClick={() => setFeedbackText(prev => prev ? `${prev}, ${example.toLowerCase()}` : example)}
                                    sx={{
                                        height: 26, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                                        bgcolor: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569',
                                        '&:hover': { bgcolor: '#f0f4ff', borderColor: '#93b0f5', color: '#23479F' },
                                        transition: 'all 0.15s ease'
                                    }}
                                />
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                            <TextField
                                fullWidth
                                multiline
                                maxRows={3}
                                size="small"
                                value={feedbackText}
                                onChange={(e) => setFeedbackText(e.target.value)}
                                placeholder="e.g. Remove the 'Tools' section, rename 'Key Takeaways' to 'Summary', add more examples to Core Concepts..."
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        if (feedbackText.trim()) {
                                            handleFeedbackSubmit(feedbackText);
                                        }
                                    }
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8f9fa', fontSize: '0.875rem' } }}
                            />
                            <Button
                                variant="contained"
                                disabled={!feedbackText.trim()}
                                onClick={() => {
                                    handleFeedbackSubmit(feedbackText);
                                }}
                                sx={{
                                    borderRadius: 2, textTransform: 'none', fontWeight: 600,
                                    minWidth: 80, height: 40,
                                    background: 'linear-gradient(135deg, #23479F 0%, #3b63c7 100%)',
                                    color: 'white',
                                    '&.Mui-disabled': {
                                        background: '#f1f5f9',
                                        color: '#94a3b8'
                                    }
                                }}
                            >
                                Apply
                            </Button>
                        </Box>
                    </Box>
                </Fade>

                {/* ── MODE: idle or regenerate → section picker + input ── */}
                <Fade in={inputMode === 'idle' || inputMode === 'regenerate' || isGenerating} timeout={300} unmountOnExit>
                    <Box sx={{ p: 2 }}>
                        {/* Section picker — show before first generation or during regeneration */}
                        {((messages.length === 0 && !isGenerating) || inputMode === 'regenerate') && (
                            <Box sx={{ maxWidth: 900, mx: 'auto', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', letterSpacing: '0.02em' }}>
                                        INCLUDE SECTIONS
                                    </Typography>
                                    <Chip
                                        label={allSectionsSelected ? 'All Selected' : `${selectedSections.length} of ${ALL_DOCUMENT_SECTIONS.length}`}
                                        size="small"
                                        onClick={() => {
                                            if (allSectionsSelected) {
                                                setSelectedSections([]);
                                            } else {
                                                setSelectedSections(ALL_DOCUMENT_SECTIONS);
                                            }
                                        }}
                                        sx={{
                                            height: 20, fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer',
                                            bgcolor: allSectionsSelected ? '#eef2ff' : '#fff7ed',
                                            color: allSectionsSelected ? '#4f46e5' : '#c2410c',
                                            border: `1px solid ${allSectionsSelected ? '#c7d2fe' : '#fdba74'}`,
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {ALL_DOCUMENT_SECTIONS.map((section) => {
                                        const isSelected = selectedSections.includes(section);
                                        return (
                                            <Chip
                                                key={section}
                                                label={section}
                                                size="small"
                                                onClick={() => {
                                                    setSelectedSections(prev =>
                                                        isSelected
                                                            ? prev.filter(s => s !== section)
                                                            : [...prev, section]
                                                    );
                                                }}
                                                sx={{
                                                    height: 24, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                                                    transition: 'all 0.15s ease',
                                                    bgcolor: isSelected ? '#eef2ff' : 'white',
                                                    color: isSelected ? '#4f46e5' : '#94a3b8',
                                                    border: `1px solid ${isSelected ? '#a5b4fc' : '#e2e8f0'}`,
                                                    '&:hover': { bgcolor: isSelected ? '#e0e7ff' : '#f8fafc', borderColor: '#a5b4fc', color: '#4f46e5' }
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}

                        {/* Regenerate header */}
                        {inputMode === 'regenerate' && (
                            <Box sx={{ maxWidth: 900, mx: 'auto', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', letterSpacing: '0.02em' }}>
                                    REGENERATE DOCUMENT
                                </Typography>
                                <Button size="small" onClick={() => setInputMode('generated')} sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.72rem', color: '#94a3b8' }}>
                                    Cancel
                                </Button>
                            </Box>
                        )}

                        <Box
                            id="feedback-submit-form"
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', maxWidth: 900, mx: 'auto' }}
                        >
                            <TextField
                                fullWidth
                                multiline
                                maxRows={4}
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                placeholder={
                                    inputMode === 'regenerate'
                                        ? "Edit your prompt if needed, or send as-is to regenerate..."
                                        : "Describe the topic you want to create a document about..."
                                }
                                variant="outlined"
                                disabled={isGenerating}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8f9fa' } }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSubmit(e);
                                    }
                                }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isGenerating || !question.trim() || (!selectedFile && (!department || !domain))}
                                sx={{
                                    borderRadius: 2, height: '56px',
                                    px: isMobile ? 2 : 3, minWidth: isMobile ? 56 : 160,
                                    boxShadow: 2, '&:hover': { boxShadow: 4 },
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    gap: 1, textTransform: 'none', fontWeight: 600
                                }}
                            >
                                {isGenerating ? (
                                    <><CircularProgress size={20} color="inherit" />{!isMobile && 'Generating...'}</>
                                ) : inputMode === 'regenerate' ? (
                                    <><RefreshIcon />{!isMobile && 'Regenerate'}</>
                                ) : (
                                    <><AutoAwesomeIcon />{!isMobile && 'Generate Document'}</>
                                )}
                            </Button>
                        </Box>

                        {(!selectedFile && (!department || !domain)) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                                Please select a file OR configure Domain and Department
                            </Typography>
                        )}
                    </Box>
                </Fade>

            </Paper>



            <Popover
                open={Boolean(anchorPosition)}
                anchorReference="anchorPosition"
                anchorPosition={anchorPosition}
                onClose={() => setAnchorPosition(null)}
            >
                <Paper sx={{ minWidth: 200 }}>
                    <List dense>
                        <ListItem>
                            <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                Improve Selected Text
                            </Typography>
                        </ListItem>
                        <Divider />
                        {improvementOptions.map((option) => (
                            <ListItem
                                key={option.type}
                                button
                                onClick={() => handleRegenerate(option.type)}
                                disabled={isRegenerating}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    {option.icon}
                                </ListItemIcon>
                                <ListItemText primary={option.label} />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Popover>
            {/* ── On-demand Section Image Generation Dialog ── */}
            <Dialog
                open={imageDialog.open}
                onClose={() => !isGeneratingImage && setImageDialog(prev => ({ ...prev, open: false }))}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{
                    fontWeight: 800, fontSize: '1.05rem', pb: 0.5,
                    display: 'flex', alignItems: 'center', gap: 1
                }}>
                    <AddPhotoIcon sx={{ color: '#6366f1', fontSize: 22 }} />
                    Add Image to Section
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                        Section: <Box component="span" sx={{ color: '#1e293b', fontWeight: 700 }}>{imageDialog.sectionName}</Box>
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2, fontSize: '0.82rem' }}>
                        Optionally describe what kind of image you want. Leave blank and AI will decide.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Additional guidance (optional)"
                        placeholder='e.g. "A flowchart showing the 5 steps of the process" or leave blank'
                        value={imageDialog.context}
                        onChange={e => setImageDialog(prev => ({ ...prev, context: e.target.value }))}
                        disabled={isGeneratingImage}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={() => setImageDialog(prev => ({ ...prev, open: false }))}
                        disabled={isGeneratingImage}
                        sx={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={sendImageRequest}
                        disabled={isGeneratingImage}
                        startIcon={isGeneratingImage
                            ? <CircularProgress size={15} color="inherit" />
                            : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)',
                            boxShadow: '0 4px 12px rgba(99,102,241,0.25)',
                            '&:hover': {
                                background: 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)'
                            }
                        }}
                    >
                        {isGeneratingImage ? 'Generating…' : 'Generate Image'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* ── Image Delete Confirmation Dialog ── */}
            <Dialog
                open={deleteConfirm.open}
                onClose={() => setDeleteConfirm({ open: false, imgId: null })}
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>Delete Image?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                        Are you sure you want to remove this image? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteConfirm({ open: false, imgId: null })} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
                    <Button onClick={confirmDeleteImage} variant="contained" color="error" sx={{ fontWeight: 700, borderRadius: 2 }}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* ── Image Lightbox Dialog ── */}
            <Dialog
                open={Boolean(lightboxSrc)}
                onClose={() => setLightboxSrc(null)}
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        background: 'transparent',
                        boxShadow: 'none',
                        m: 2,
                        position: 'relative'
                    }
                }}
            >
                {lightboxSrc && (
                    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <IconButton
                            onClick={() => setLightboxSrc(null)}
                            sx={{
                                position: 'absolute',
                                top: -40,
                                right: -40,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <img
                            src={lightboxSrc}
                            alt="Fullscreen view"
                            style={{ maxWidth: '100vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px' }}
                        />
                    </Box>
                )}
            </Dialog>
        </Box>
    );
};

export default ContentGeneration;